import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  User, Mail, Phone, CreditCard, MapPin, Lock, Trash2, 
  Check, AlertCircle, ChevronRight, Edit2, Plus, Star,
  Eye, EyeOff, Loader2, Shield, X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountSettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isLoading: authLoading, logout, checkAuth } = useAuth();
  
  // General state
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Profile state
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  
  // Password state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Addresses state
  const [addresses, setAddresses] = useState([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [addressFormData, setAddressFormData] = useState({
    label: 'Home',
    name: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    is_default: false
  });
  
  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  
  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Check for payment update success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const isUpdate = searchParams.get('update');
    
    if (sessionId && isUpdate === 'true') {
      completePaymentUpdate(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch data on mount
  useEffect(() => {
    if (isAuthenticated && user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || ''
      });
      fetchAddresses();
      fetchPaymentMethod();
    }
  }, [isAuthenticated, user]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/settings' } } });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setErrorMessage('');
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const showError = (message) => {
    setErrorMessage(message);
    setSuccessMessage('');
    setTimeout(() => setErrorMessage(''), 5000);
  };

  // Fetch addresses
  const fetchAddresses = async () => {
    setIsLoadingAddresses(true);
    try {
      const response = await axios.get(`${API}/addresses`);
      setAddresses(response.data);
    } catch (err) {
      console.error('Failed to fetch addresses:', err);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  // Fetch payment method
  const fetchPaymentMethod = async () => {
    setIsLoadingPayment(true);
    try {
      const response = await axios.get(`${API}/payments/saved-method`);
      setPaymentMethod(response.data);
    } catch (err) {
      console.error('Failed to fetch payment method:', err);
    } finally {
      setIsLoadingPayment(false);
    }
  };

  // Update profile
  const handleUpdateProfile = async () => {
    setIsLoading(true);
    try {
      await axios.patch(`${API}/account/profile`, profileData);
      await checkAuth(); // Refresh user data
      setIsEditingProfile(false);
      showSuccess('Profile updated successfully');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      showError('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      showError('Password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/account/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setShowPasswordForm(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      showSuccess('Password changed successfully');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  // Add/Edit address
  const handleSaveAddress = async () => {
    if (!addressFormData.name || !addressFormData.street_address || !addressFormData.city || !addressFormData.state || !addressFormData.zip_code) {
      showError('Please fill in all required fields');
      return;
    }
    
    setIsLoading(true);
    try {
      if (editingAddressId) {
        await axios.patch(`${API}/addresses/${editingAddressId}`, addressFormData);
        showSuccess('Address updated successfully');
      } else {
        await axios.post(`${API}/addresses`, addressFormData);
        showSuccess('Address added successfully');
      }
      await fetchAddresses();
      resetAddressForm();
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to save address');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete address
  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    
    setIsLoading(true);
    try {
      await axios.delete(`${API}/addresses/${addressId}`);
      await fetchAddresses();
      showSuccess('Address deleted successfully');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to delete address');
    } finally {
      setIsLoading(false);
    }
  };

  // Make address default
  const handleMakeDefault = async (addressId) => {
    setIsLoading(true);
    try {
      await axios.patch(`${API}/addresses/${addressId}`, { is_default: true });
      await fetchAddresses();
      showSuccess('Default address updated');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to update default address');
    } finally {
      setIsLoading(false);
    }
  };

  // Edit address
  const handleEditAddress = (address) => {
    setAddressFormData({
      label: address.label,
      name: address.name,
      street_address: address.street_address,
      city: address.city,
      state: address.state,
      zip_code: address.zip_code,
      phone: address.phone || '',
      is_default: address.is_default
    });
    setEditingAddressId(address.id);
    setShowAddressForm(true);
  };

  // Reset address form
  const resetAddressForm = () => {
    setAddressFormData({
      label: 'Home',
      name: '',
      street_address: '',
      city: '',
      state: '',
      zip_code: '',
      phone: '',
      is_default: false
    });
    setEditingAddressId(null);
    setShowAddressForm(false);
  };

  // Update payment method
  const handleUpdatePaymentMethod = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/payments/update-method`, {}, {
        headers: { 'Origin': window.location.origin }
      });
      window.location.href = response.data.checkout_url;
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to update payment method');
      setIsLoading(false);
    }
  };

  // Complete payment method update
  const completePaymentUpdate = async (sessionId) => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/payments/complete-update?session_id=${sessionId}`);
      await fetchPaymentMethod();
      showSuccess('Payment method updated successfully');
      // Clear URL params
      navigate('/settings', { replace: true });
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to complete payment update');
    } finally {
      setIsLoading(false);
    }
  };

  // Remove payment method
  const handleRemovePaymentMethod = async () => {
    if (!window.confirm('Are you sure you want to remove your saved payment method? You\'ll need to enter payment info for future submissions.')) return;
    
    setIsLoading(true);
    try {
      await axios.delete(`${API}/payments/saved-method`);
      await fetchPaymentMethod();
      showSuccess('Payment method removed');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to remove payment method');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      showError('Please type DELETE to confirm');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.delete(`${API}/account`);
      logout();
      navigate('/');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to delete account');
      setIsLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-[#003594] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <main className="flex-1 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Link to="/dashboard" className="text-sm text-gray-500 hover:text-[#003594]">
              Dashboard
            </Link>
            <span className="mx-2 text-gray-300">/</span>
            <span className="text-sm text-gray-900 font-medium">Settings</span>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-8">Account Settings</h1>

          {/* Success/Error Messages */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
              <p className="text-green-700">{successMessage}</p>
            </div>
          )}
          {errorMessage && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-600">{errorMessage}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Profile Section */}
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-testid="profile-section">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#003594]/10 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-[#003594]" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Account Information</h2>
                </div>
                {!isEditingProfile && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setIsEditingProfile(true)}
                    className="text-[#003594]"
                    data-testid="edit-profile-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>

              {isEditingProfile ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Name</Label>
                    <Input
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      className="border-gray-300"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Email</Label>
                    <Input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      className="border-gray-300"
                      data-testid="profile-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Phone</Label>
                    <Input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="border-gray-300"
                      data-testid="profile-phone-input"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button 
                      onClick={handleUpdateProfile} 
                      disabled={isLoading}
                      className="bg-[#003594] hover:bg-[#002554]"
                      data-testid="save-profile-btn"
                    >
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Save Changes
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setIsEditingProfile(false);
                        setProfileData({
                          name: user?.name || '',
                          email: user?.email || '',
                          phone: user?.phone || ''
                        });
                      }}
                      data-testid="cancel-profile-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 py-2">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-medium text-gray-900">{user?.name || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 py-2">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 py-2">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{user?.phone || 'Not set'}</p>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Saved Addresses Section */}
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-testid="addresses-section">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#003594]/10 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#003594]" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Saved Shipping Addresses</h2>
                </div>
                {!showAddressForm && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowAddressForm(true)}
                    className="text-[#003594]"
                    data-testid="add-address-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New
                  </Button>
                )}
              </div>

              {isLoadingAddresses ? (
                <div className="py-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-[#003594] mx-auto" />
                </div>
              ) : showAddressForm ? (
                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-gray-900">
                      {editingAddressId ? 'Edit Address' : 'Add New Address'}
                    </h3>
                    <button onClick={resetAddressForm} className="text-gray-400 hover:text-gray-600">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-700 text-sm">Label</Label>
                        <Input
                          value={addressFormData.label}
                          onChange={(e) => setAddressFormData({ ...addressFormData, label: e.target.value })}
                          placeholder="Home, Office, etc."
                          className="border-gray-300"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 text-sm">Full Name *</Label>
                        <Input
                          value={addressFormData.name}
                          onChange={(e) => setAddressFormData({ ...addressFormData, name: e.target.value })}
                          className="border-gray-300"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">Street Address *</Label>
                      <Input
                        value={addressFormData.street_address}
                        onChange={(e) => setAddressFormData({ ...addressFormData, street_address: e.target.value })}
                        className="border-gray-300"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-700 text-sm">City *</Label>
                        <Input
                          value={addressFormData.city}
                          onChange={(e) => setAddressFormData({ ...addressFormData, city: e.target.value })}
                          className="border-gray-300"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 text-sm">State *</Label>
                        <Input
                          value={addressFormData.state}
                          onChange={(e) => setAddressFormData({ ...addressFormData, state: e.target.value })}
                          className="border-gray-300"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 text-sm">ZIP *</Label>
                        <Input
                          value={addressFormData.zip_code}
                          onChange={(e) => setAddressFormData({ ...addressFormData, zip_code: e.target.value })}
                          className="border-gray-300"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 text-sm">Phone</Label>
                      <Input
                        value={addressFormData.phone}
                        onChange={(e) => setAddressFormData({ ...addressFormData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="border-gray-300"
                      />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button 
                        onClick={handleSaveAddress} 
                        disabled={isLoading}
                        className="bg-[#003594] hover:bg-[#002554]"
                        data-testid="save-address-btn"
                      >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        {editingAddressId ? 'Update Address' : 'Save Address'}
                      </Button>
                      <Button variant="outline" onClick={resetAddressForm}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              ) : addresses.length === 0 ? (
                <div className="py-8 text-center">
                  <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No saved addresses</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowAddressForm(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Address
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {addresses.map((addr) => (
                    <div 
                      key={addr.id} 
                      className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors"
                      data-testid={`address-${addr.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900">{addr.label}</span>
                            {addr.is_default && (
                              <span className="inline-flex items-center gap-1 text-xs bg-[#003594]/10 text-[#003594] px-2 py-0.5 rounded-full">
                                <Star className="w-3 h-3" />
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-gray-600 text-sm">{addr.name}</p>
                          <p className="text-gray-600 text-sm">{addr.street_address}</p>
                          <p className="text-gray-600 text-sm">{addr.city}, {addr.state} {addr.zip_code}</p>
                          {addr.phone && <p className="text-gray-500 text-sm">{addr.phone}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEditAddress(addr)}
                            className="text-gray-600"
                          >
                            Edit
                          </Button>
                          {!addr.is_default && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleMakeDefault(addr.id)}
                              className="text-[#003594]"
                            >
                              Make Default
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteAddress(addr.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Payment Method Section */}
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-testid="payment-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#003594]/10 rounded-full flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-[#003594]" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Payment Method</h2>
              </div>

              {isLoadingPayment ? (
                <div className="py-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-[#003594] mx-auto" />
                </div>
              ) : paymentMethod?.has_saved_method ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                      <CreditCard className="w-6 h-6 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {paymentMethod.payment_method_brand} ending in {paymentMethod.payment_method_last4}
                      </p>
                      <p className="text-sm text-gray-500">Saved for future submissions</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-500" />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={handleUpdatePaymentMethod}
                      disabled={isLoading}
                      data-testid="update-payment-btn"
                    >
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Update Payment Method
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleRemovePaymentMethod}
                      disabled={isLoading}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      data-testid="remove-payment-btn"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium mb-1">No payment method saved</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Add a payment method during your next submission
                  </p>
                  <Link to="/new-submission">
                    <Button className="bg-[#003594] hover:bg-[#002554]">
                      Start New Submission
                    </Button>
                  </Link>
                </div>
              )}
            </section>

            {/* Password Section */}
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-testid="password-section">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#003594]/10 rounded-full flex items-center justify-center">
                    <Lock className="w-5 h-5 text-[#003594]" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Password</h2>
                </div>
                {!showPasswordForm && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowPasswordForm(true)}
                    className="text-[#003594]"
                    data-testid="change-password-btn"
                  >
                    Change Password
                  </Button>
                )}
              </div>

              {showPasswordForm ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Current Password</Label>
                    <div className="relative">
                      <Input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                        className="border-gray-300 pr-10"
                        data-testid="current-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">New Password</Label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                        className="border-gray-300 pr-10"
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">Must be at least 6 characters</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Confirm New Password</Label>
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      className="border-gray-300"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button 
                      onClick={handleChangePassword} 
                      disabled={isLoading}
                      className="bg-[#003594] hover:bg-[#002554]"
                      data-testid="save-password-btn"
                    >
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Update Password
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 py-2">
                  <Shield className="w-5 h-5 text-gray-400" />
                  <p className="text-gray-600">••••••••••••</p>
                </div>
              )}
            </section>

            {/* Account Actions Section */}
            <section className="bg-white rounded-2xl shadow-sm border border-red-100 p-6" data-testid="danger-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-500" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Danger Zone</h2>
              </div>

              {showDeleteConfirm ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <h3 className="font-semibold text-red-800 mb-2">Delete Your Account</h3>
                  <p className="text-sm text-red-700 mb-4">
                    This will permanently delete your account and all associated data including submissions, addresses, and payment information. This action cannot be undone.
                  </p>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-red-700 text-sm">Type DELETE to confirm</Label>
                      <Input
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="DELETE"
                        className="border-red-300 focus:ring-red-500"
                        data-testid="delete-confirm-input"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button 
                        variant="destructive"
                        onClick={handleDeleteAccount}
                        disabled={isLoading || deleteConfirmText !== 'DELETE'}
                        data-testid="confirm-delete-btn"
                      >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Delete My Account
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteConfirmText('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-gray-600 mb-4">
                    Once you delete your account, there is no going back. Please be certain.
                  </p>
                  <Button 
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    data-testid="delete-account-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Account
                  </Button>
                </div>
              )}

              {/* Support Link */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Need help? Contact us at{' '}
                  <a href="mailto:support@nicegrading.com" className="text-[#003594] hover:underline">
                    support@nicegrading.com
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AccountSettingsPage;
